import React, { useState, useEffect, useCallback } from 'react';
import ManagementLayout from '../layout/ManagementLayout';
import LoadingState from '../common/LoadingState';
import ErrorState from '../common/ErrorState';
import { useConfirm } from '../common/ConfirmModal';
import {
  fetchUsers, createUser, updateUser, deleteUser,
  fetchCustomers, createCustomer, deleteCustomer,
  fetchFolders, createFolder, deleteFolder,
} from '../../api/adminApi';

const TABS = ['Users', 'Customers', 'Folders'];

export default function AdminConfig() {
  const [activeTab, setActiveTab] = useState('Users');

  return (
    <ManagementLayout>
      <div className="mol-page">
        <div className="mol-page__header">
          <div>
            <h2 className="mol-page__title">Admin Configuration</h2>
            <p className="mol-page__subtitle">Manage users, customers, and folder structure</p>
          </div>
        </div>
        <div className="mol-admin-tabs">
          {TABS.map((tab) => (
            <button
              key={tab}
              className={`mol-admin-tabs__btn ${activeTab === tab ? 'mol-admin-tabs__btn--active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
        {activeTab === 'Users' && <UsersPanel />}
        {activeTab === 'Customers' && <CustomersPanel />}
        {activeTab === 'Folders' && <FoldersPanel />}
      </div>
    </ManagementLayout>
  );
}

/* ===================== USERS PANEL ===================== */
function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState({ email: '', customerSlug: '', role: 'viewer' });
  const [saving, setSaving] = useState(false);
  const [confirm, ConfirmDialog] = useConfirm();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [usersRes, custsRes] = await Promise.all([fetchUsers(), fetchCustomers()]);
      setUsers(usersRes.data.users);
      setCustomers(custsRes.data.customers);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.email || !form.customerSlug) return;
    try {
      setSaving(true);
      await createUser(form.email, form.customerSlug, form.role);
      setForm({ email: '', customerSlug: '', role: 'viewer' });
      setShowForm(false);
      await load();
    } catch (err) {
      alert('Failed to create user: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRoleChange = async (username, newRole) => {
    try {
      await updateUser(username, { role: newRole });
      setUsers((prev) => prev.map((u) => u.username === username ? { ...u, role: newRole } : u));
    } catch (err) {
      alert('Failed to update role: ' + err.message);
    }
  };

  const handleCustomerChange = async (username, newSlug) => {
    try {
      await updateUser(username, { customerSlug: newSlug });
      setUsers((prev) => prev.map((u) => u.username === username ? { ...u, customerSlug: newSlug } : u));
    } catch (err) {
      alert('Failed to update customer: ' + err.message);
    }
  };

  const handleDeleteUser = async (username) => {
    const ok = await confirm({
      title: 'Delete User',
      message: `Delete user "${username}"? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteUser(username);
      setUsers((prev) => prev.filter((u) => u.username !== username));
    } catch (err) {
      alert('Failed to delete user: ' + err.message);
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <div className="mol-admin-panel">
      <div className="mol-admin-panel__header">
        <h3>Cognito Users ({users.length})</h3>
        <button className="mol-btn mol-btn--primary mol-btn--sm" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ New User'}
        </button>
      </div>

      {showForm && (
        <form className="mol-admin-form" onSubmit={handleCreate}>
          <div className="mol-admin-form__row">
            <input
              type="email"
              placeholder="Email address"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="mol-admin-form__input"
              required
            />
            <select
              value={form.customerSlug}
              onChange={(e) => setForm({ ...form, customerSlug: e.target.value })}
              className="mol-admin-form__select"
              required
            >
              <option value="">Select customer...</option>
              <option value="*">* (Admin - All Customers)</option>
              {customers.map((c) => (
                <option key={c.slug} value={c.slug}>{c.name} ({c.slug})</option>
              ))}
            </select>
            <select
              value={form.role}
              onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="mol-admin-form__select"
            >
              <option value="viewer">Viewer</option>
              <option value="uploader">Uploader</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
            </select>
            <button type="submit" className="mol-btn mol-btn--primary mol-btn--sm" disabled={saving}>
              {saving ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      )}

      <div className="mol-admin-table-wrap">
        <table className="mol-admin-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Username</th>
              <th>Customer</th>
              <th>Role</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.username}>
                <td>{u.email}</td>
                <td className="mol-admin-table__mono">{u.username}</td>
                <td>
                  <select
                    value={u.customerSlug || ''}
                    onChange={(e) => handleCustomerChange(u.username, e.target.value)}
                    className="mol-admin-form__select mol-admin-form__select--inline"
                  >
                    <option value="">— None —</option>
                    <option value="*">* (All Customers)</option>
                    {customers.map((c) => (
                      <option key={c.slug} value={c.slug}>{c.slug}</option>
                    ))}
                  </select>
                </td>
                <td>
                  <select
                    value={u.role}
                    onChange={(e) => handleRoleChange(u.username, e.target.value)}
                    className="mol-admin-form__select mol-admin-form__select--inline"
                  >
                    <option value="viewer">viewer</option>
                    <option value="uploader">uploader</option>
                    <option value="manager">manager</option>
                    <option value="admin">admin</option>
                  </select>
                </td>
                <td>
                  <span className={`mol-badge mol-badge--${u.status === 'CONFIRMED' || u.status === 'EXTERNAL_PROVIDER' ? 'active' : 'pending'}`}>
                    {u.status}
                  </span>
                </td>
                <td>
                  <button
                    className="mol-btn mol-btn--danger mol-btn--sm"
                    onClick={() => handleDeleteUser(u.username)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ConfirmDialog />
    </div>
  );
}

/* ===================== CUSTOMERS PANEL ===================== */
function CustomersPanel() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', slug: '' });
  const [saving, setSaving] = useState(false);
  const [confirm, ConfirmDialog] = useConfirm();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchCustomers();
      setCustomers(res.data.customers);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleNameChange = (val) => {
    const slug = val.toLowerCase().replace(/[^a-z0-9\s-]/g, '').replace(/[\s_]+/g, '-').trim();
    setForm({ name: val, slug });
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name || !form.slug) return;
    try {
      setSaving(true);
      await createCustomer(form.name, form.slug);
      setForm({ name: '', slug: '' });
      setShowForm(false);
      await load();
    } catch (err) {
      alert('Failed to create customer: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slug, name) => {
    const ok = await confirm({
      title: 'Remove Organization',
      message: `Remove organization "${name}" (${slug})? This only removes the record, not S3 data.`,
      confirmLabel: 'Remove',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteCustomer(slug);
      await load();
    } catch (err) {
      alert('Failed to delete: ' + err.message);
    }
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <div className="mol-admin-panel">
      <div className="mol-admin-panel__header">
        <h3>Customers ({customers.length})</h3>
        <button className="mol-btn mol-btn--primary mol-btn--sm" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ New Customer'}
        </button>
      </div>

      {showForm && (
        <form className="mol-admin-form" onSubmit={handleCreate}>
          <div className="mol-admin-form__row">
            <input
              type="text"
              placeholder="Customer name"
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              className="mol-admin-form__input"
              required
            />
            <input
              type="text"
              placeholder="slug (auto-generated)"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              className="mol-admin-form__input"
              required
            />
            <button type="submit" className="mol-btn mol-btn--primary mol-btn--sm" disabled={saving}>
              {saving ? 'Creating...' : 'Create'}
            </button>
          </div>
          <p className="mol-admin-form__hint">S3 folder: <code>{form.slug}/</code></p>
        </form>
      )}

      <div className="mol-admin-table-wrap">
        <table className="mol-admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Slug</th>
              <th>S3 Prefix</th>
              <th>Plan</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c.id}>
                <td><strong>{c.name}</strong></td>
                <td className="mol-admin-table__mono">{c.slug}</td>
                <td className="mol-admin-table__mono">{c.s3Prefix}</td>
                <td><span className="mol-badge">{c.plan}</span></td>
                <td>
                  <span className={`mol-badge mol-badge--${c.status === 'active' ? 'active' : 'pending'}`}>
                    {c.status}
                  </span>
                </td>
                <td>
                  <button className="mol-btn mol-btn--danger mol-btn--xs" onClick={() => handleDelete(c.slug, c.name)}>
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ConfirmDialog />
    </div>
  );
}

/* ===================== FOLDERS PANEL ===================== */
function FoldersPanel() {
  const [folders, setFolders] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPrefix, setCurrentPrefix] = useState('');
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [folderName, setFolderName] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirm, ConfirmDialog] = useConfirm();

  const load = useCallback(async (prefix = '') => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetchFolders(prefix);
      setFolders(res.data.folders || []);
      setFiles(res.data.files || []);
      setCurrentPrefix(prefix);

      // Build breadcrumbs
      const parts = prefix.split('/').filter(Boolean);
      const crumbs = [{ label: 'Root', prefix: '' }];
      let running = '';
      for (const part of parts) {
        running += part + '/';
        crumbs.push({ label: part, prefix: running });
      }
      setBreadcrumbs(crumbs);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(''); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!folderName) return;
    try {
      setSaving(true);
      await createFolder(currentPrefix, folderName);
      setFolderName('');
      setShowForm(false);
      await load(currentPrefix);
    } catch (err) {
      alert('Failed to create folder: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFolder = async (e, prefix, name) => {
    e.stopPropagation();
    const ok = await confirm({
      title: 'Delete Folder',
      message: `Delete folder "${name}" and ALL its contents? This cannot be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await deleteFolder(prefix);
      await load(currentPrefix);
    } catch (err) {
      alert('Failed to delete folder: ' + err.message);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    if (bytes < 1073741824) return (bytes / 1048576).toFixed(1) + ' MB';
    return (bytes / 1073741824).toFixed(2) + ' GB';
  };

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} onRetry={() => load(currentPrefix)} />;

  return (
    <div className="mol-admin-panel">
      <div className="mol-admin-panel__header">
        <div className="mol-admin-breadcrumbs">
          {breadcrumbs.map((crumb, i) => (
            <span key={crumb.prefix}>
              {i > 0 && <span className="mol-admin-breadcrumbs__sep">/</span>}
              <button
                className={`mol-admin-breadcrumbs__btn ${i === breadcrumbs.length - 1 ? 'mol-admin-breadcrumbs__btn--active' : ''}`}
                onClick={() => load(crumb.prefix)}
              >
                {crumb.label}
              </button>
            </span>
          ))}
        </div>
        <button className="mol-btn mol-btn--primary mol-btn--sm" onClick={() => setShowForm(!showForm)}>
          {showForm ? 'Cancel' : '+ New Folder'}
        </button>
      </div>

      {showForm && (
        <form className="mol-admin-form" onSubmit={handleCreate}>
          <div className="mol-admin-form__row">
            <span className="mol-admin-form__prefix">{currentPrefix || '/'}</span>
            <input
              type="text"
              placeholder="Folder name"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              className="mol-admin-form__input"
              required
            />
            <button type="submit" className="mol-btn mol-btn--primary mol-btn--sm" disabled={saving}>
              {saving ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      )}

      <div className="mol-admin-table-wrap">
        <table className="mol-admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Type</th>
              <th>Size</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {folders.map((f) => (
              <tr key={f.key} className="mol-admin-table__row--clickable" onClick={() => load(f.key)}>
                <td>
                  <span className="mol-admin-folder-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                  </span>
                  {f.name}
                </td>
                <td>Folder</td>
                <td>—</td>
                <td>
                  <button
                    className="mol-btn mol-btn--danger mol-btn--xs"
                    onClick={(e) => handleDeleteFolder(e, f.key, f.name)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
            {files.map((f) => (
              <tr key={f.key}>
                <td className="mol-admin-table__mono">{f.name}</td>
                <td>{f.name.split('.').pop().toUpperCase()}</td>
                <td>{formatSize(f.size)}</td>
                <td></td>
              </tr>
            ))}
            {folders.length === 0 && files.length === 0 && (
              <tr><td colSpan={4} className="mol-admin-table__empty">Empty folder</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <ConfirmDialog />
    </div>
  );
}
